// THE canonical client-side validation pipeline (D-026). Both the app UI (src/main.ts) and the
// CI parity harness (testsuite/app-parity/) call this exact function — the parity gate therefore
// tests the REAL app, not a parallel copy. No Node, no network beyond same-origin engine assets.
//
// Verdict model (D-017, matches the KoSIT oracle): a refereeable file is `invalid` iff it is
// XSD-invalid OR has any non-information Schematron assert OR the Schematron engine crashed
// (D-024). Non-refereeable files get `undetermined` — we never judge against the wrong
// benchmark (D-010/D-022).

import { detect, looksLikePdf } from './detect';
import { extractXmlFromPdf } from './pdf';
import { validateXsd } from './xsd';
import { validateSchematron } from './schematron';
import { renderInvoice } from './render';
import { explainFinding } from './rule-explanations';
import type {
  Detection,
  Finding,
  ValidateInput,
  ValidateOptions,
  ValidationResult,
} from './types';

function emptyDetection(container: 'xml' | 'pdf'): Detection {
  return {
    container,
    syntax: null,
    docType: 'unknown',
    profileId: null,
    profileClass: 'unknown',
    refereeable: false,
  };
}

export async function validate(
  input: ValidateInput,
  opts: ValidateOptions = {},
): Promise<ValidationResult> {
  const render = opts.render ?? true;
  const container = looksLikePdf(input.bytes) ? 'pdf' : 'xml';

  try {
    // 1. Get the XML — extract from the PDF/A-3 container when needed.
    let xml: string;
    let attachmentName: string | undefined;
    if (container === 'pdf') {
      const ex = await extractXmlFromPdf(input.bytes);
      xml = ex.xml;
      attachmentName = ex.attachmentName;
    } else {
      xml = new TextDecoder('utf-8').decode(input.bytes);
    }

    // 2. Detect syntax / document type / profile.
    const detection = detect(xml, container, attachmentName);
    const syntax = detection.syntax!; // detectSyntax always returns UBL or CII

    // 3. Non-refereeable profile: render what we can, assert no verdict (D-010/D-022).
    if (!detection.refereeable) {
      const renderedHtml = render ? await renderInvoice(xml, syntax, detection.docType) : null;
      return {
        verdict: 'undetermined',
        detection,
        findings: [],
        ruleIds: [],
        xsdValid: null,
        schematronCrashed: null,
        renderedHtml,
      };
    }

    // 4. Full validation: XSD + Schematron (IBAN-corrected). Run concurrently.
    const [xsd, schematron] = await Promise.all([
      validateXsd(xml, syntax),
      validateSchematron(xml, syntax),
    ]);

    // `crashed !== null` (the type contract), NOT truthiness: a crash with an empty message
    // (crashed === '') must still count as invalid — never fail open on an uncheckable file (D-024).
    const verdict =
      !xsd.valid || schematron.hasNonInformationAssert || schematron.crashed !== null
        ? 'invalid'
        : 'valid';

    const findings: Finding[] = [...xsdFindings(xsd.errors), ...schematron.findings].map((f) => ({
      ...f,
      plainDe: explainFinding(f.ruleId),
    }));
    // The D-009 rule-ID signal is the Schematron non-information set (matches the Phase-1 harness).
    const ruleIds = [
      ...new Set(
        schematron.findings.filter((f) => f.severity !== 'information').map((f) => f.ruleId),
      ),
    ];

    const renderedHtml = render ? await renderInvoice(xml, syntax, detection.docType) : null;

    return {
      verdict,
      detection,
      findings,
      ruleIds,
      xsdValid: xsd.valid,
      schematronCrashed: schematron.crashed,
      renderedHtml,
    };
  } catch (e) {
    return {
      verdict: 'undetermined',
      detection: emptyDetection(container),
      findings: [],
      ruleIds: [],
      xsdValid: null,
      schematronCrashed: null,
      renderedHtml: null,
      error: String((e as Error)?.message ?? e),
    };
  }
}

// XSD parser errors → findings. They carry no rule ID; xmllint prefixes cvc-* codes in the text.
function xsdFindings(errors: string[]): Finding[] {
  return errors.map((message) => {
    const code = (message.match(/\b(cvc-[\w.-]+)\b/) ?? [])[1] ?? 'XSD';
    return { ruleId: code, severity: 'fatal' as const, officialMessage: message };
  });
}
